import VDeviceMixin from './mixin';
import { nanoid } from 'nanoid';
import get from 'lodash/get';
import isObject from 'lodash/isObject';
import isEqual from 'lodash/isEqual';
import pullAt from 'lodash/pullAt';
import cloneDeep from 'lodash/cloneDeep';
import ApiService from '@/services/apiService';
import { mapGetters } from 'vuex';
import { createComparisonOperatorOptions, filterComparisonOperators } from '@/utils/comparisonOperators';
export default {
  name: 'CoreDtwinStateVirtualDeviceAggregateHealth',
  mixins: [VDeviceMixin],
  props: {
    value: {
      type: Object,
      required: true
    },
    lclModel: {
      type: Object,
      required: false,
      default: () => ({})
    },
    usage: {
      type: String,
      required: false,
      // is either used in the `state` or in the `device-type`
      default: 'state'
    },
    // the whole device-type object, which should include the parser
    // ONLY relevant, if embedded into the device-type info
    deviceTypeObject: {
      type: Object,
      required: false,
      default: () => ({})
    },
    // the original twin(/virtual-device)-model
    // to be used by some components for additional requesting data
    // ONLY relevant, if embedded into the twin-state-edit
    twinModel: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data: function () {
    return {
      healthMetrics: [],
      // a UI-proxy for the switch, in order to catch changes and "abort" via a prompt
      // if the prompt is accepted, forward to the actual `extendedMode` attr
      triggerExtendedModeSwitch: false,
      switchToWizardMode: false,
      extendedMode: false,
      deviceTypeHealthConfig: null,
      deviceDriverHealthConfig: null
    };
  },
  computed: {
    ...mapGetters({
      isFeatureFlagEnabled: 'app/isFeatureFlagEnabled'
    }),
    formIsValid() {
      return true;
    },
    vDeviceHealthStates() {
      if (this.usage === 'state') {
        return this.otherStates.map(this.mapStateToUi);
      }
      return this.deviceTypeVariables();
    },
    virtualDeviceConfigStates() {
      if (this.usage === 'state') {
        return get(this.value, 'stateIds');
      }
      return get(this.value, 'stateKeys');
    },
    stateObjects() {
      return this.vDeviceHealthStates;
    },
    // jsHeaderPreview() {
    //   return `function (${(this.selectedVirtualDeviceState || [])
    //     .map((s) => {
    //       return s.key;
    //     })
    //     .join(', ')}) {`;
    // },
    identifierRules() {
      return [v => !!v && this.metricIdentifierIsUnique(v) || this.$i18n_t('DigitalTwin.VirtualDevice.state.health.identifierUnique')];
    },
    publishValue() {
      const publish = {
        health: cloneDeep(this.healthMetrics)
      };
      if (this.usage === 'state') {
        const stateIds = (this.selectedVirtualDeviceState || []).map(s => s.id);
        // simply use twin-id of any other state
        publish.id = get(this.otherStates, '[0].dtwinId');
        publish.stateIds = stateIds;
      } else {
        const stateKeys = (this.selectedVirtualDeviceState || []).map(s => s.key);
        publish.stateKeys = stateKeys;
      }
      return publish;
    },
    availableDurations() {
      const baseTranslation = 'DigitalTwin.DigitalTwin.visualization.timeRanges.count';
      return [15, 30].map(min => {
        return {
          value: min * 60,
          text: this.$i18n_tc(`${baseTranslation}Min`, min, {
            n: min
          })
        };
      }).concat([1, 3, 6, 12, 24].map(hour => {
        return {
          value: hour * 3600,
          text: this.$i18n_tc(`${baseTranslation}Hour`, hour, {
            n: hour
          })
        };
      })).concat([2, 7, 14].map(day => {
        return {
          value: day * 86400,
          text: this.$i18n_tc(`${baseTranslation}Day`, day, {
            n: day
          })
        };
      })).concat([1, 3, 6].map(mon => {
        return {
          value: mon * 30 * 86400,
          text: this.$i18n_tc(`${baseTranslation}Month`, mon, {
            n: mon
          })
        };
      })).concat([{
        value: 365 * 86400,
        text: this.$i18n_t('DigitalTwin.DigitalTwin.visualization.timeRanges.1Y')
      }]);
    },
    healthOperatorBaseOptions() {
      return createComparisonOperatorOptions((...args) => this.$i18n_t(...args), {
        includeValueOlderThan: true
      });
    },
    stateIdentAttr() {
      // which "identifier" of the state-object shall be used?
      // in the device-type health-config it is just a template, so use the key here,
      // since obviously the final state-id at the corresponding VD does not yet exist at that point
      return this.usage === 'state' ? 'id' : 'key';
    },
    isDeviceDriverActive() {
      return !this.isFeatureFlagEnabled('env_dispatcher_devicetype_fallback');
    }
  },
  watch: {
    triggerExtendedModeSwitch() {
      if (!this.triggerExtendedModeSwitch) {
        // switching from extended-mode to wizard-mode
        // warn user, that the custom scripts will be lost
        this.switchToWizardMode = true;
        return;
      }
      this.doChangeExtendedMode();
    }
  },
  async mounted() {
    this.healthMetrics = cloneDeep(get(this.value, 'health') || []);
    this.extendedMode = this.healthMetrics.length > 0 && this.healthMetrics.find(metric => metric.wizard) === undefined;
    this.triggerExtendedModeSwitch = this.extendedMode;
    let deviceTypeId = get(this.twinModel, 'data.virtualDevice.deviceTypeId');
    let deviceDriverId = get(this.twinModel, 'data.virtualDevice.deviceDriverId');
    if (this.isDeviceDriverActive) {
      deviceDriverId = deviceDriverId || deviceTypeId;
      deviceTypeId = null;
    }
    try {
      if (deviceTypeId) {
        await this.fetchDeviceTypeHealthConfig(deviceTypeId);
      } else if (deviceDriverId) {
        await this.fetchDeviceDriverHealthConfig(deviceDriverId);
      }
    } catch (e) {
      console.error(e);
    }
  },
  methods: {
    onWiredStateChange(metric) {
      const validOperators = this.operatorOptionsForMetric(metric).map(option => option.value);
      if (metric.operator && !validOperators.includes(metric.operator)) {
        metric.operator = undefined;
        metric.value = undefined;
      }
      this.emitSelection();
    },
    operatorOptionsForMetric(metric) {
      const state = metric ? this.mapStateToPersistRepresentation(metric.wiredState) : null;
      const filtered = filterComparisonOperators(this.healthOperatorBaseOptions, {
        valueType: get(state, 'properties.valueType', 'number')
      });
      return filtered.map(option => ({
        text: option.label,
        value: option.value
      }));
    },
    async fetchDeviceTypeHealthConfig(id) {
      const config = await ApiService.virtualDeviceGetDeviceTypeHealthConfig(id);
      const confValue = (config || {}).value;
      this.deviceTypeHealthConfig = confValue;
    },
    async fetchDeviceDriverHealthConfig(id) {
      const config = await ApiService.virtualDeviceGetDeviceDriverHealthConfig(id);
      this.deviceDriverHealthConfig = config || {};
    },
    doChangeExtendedMode() {
      this.switchToWizardMode = false;
      this.extendedMode = this.triggerExtendedModeSwitch;
      this.emitSelection();
    },
    mapStateToPersistRepresentation(state) {
      if (this.usage === 'state') {
        return this.vDeviceHealthStates.find(s => s.id === state);
      }
      return this.vDeviceHealthStates.find(s => s.key === state);
    },
    createUniqueMetricKey(metric, index, count) {
      // for switching between extendedMode and wizard-mode,
      // try to keep the original identifier, if one was set
      metric.identifier = metric.identifier || `metric_${index}`;
      if (!this.metricIdentifierIsUnique(metric.identifier)) {
        // first try with the maximum index; new metrics are added at position 0, so they may
        // overlap with the initial "metric_0"
        if (metric.identifier === `metric_${index}`) {
          metric.identifier = `metric_${count - 1}`;
        }
        if (!this.metricIdentifierIsUnique(metric.identifier)) {
          // force unique identifier then
          metric.identifier = `${metric.identifier}${nanoid()}`;
        }
      }
    },
    jsTransformerFromWizard(metric, wiredStateObj) {
      const metricValueIsNumber = value => {
        return !isNaN(parseFloat(value)) && `${parseFloat(value)}` === `${value}`;
      };

      // in order to easier switch back to the extended mode, update the JS-transformer properly as well
      switch (metric.operator) {
        case 'VALUE_OLDER_THAN':
          metric.jsTransformer = ` return new Date() - new Date(params["${wiredStateObj.key}"].time) > ${metric.value * 1000}`;
          break;
        case 'EQUALS':
        case 'NOT_EQUALS':
          metric.jsTransformer = ` return params["${wiredStateObj.key}"].value ${metric.operator === 'EQUALS' ? '=' : '!'}= "${metric.value}" `;
          break;
        case 'GREATER_THAN':
        case 'LESS_THAN':
          const compValue = metricValueIsNumber(metric.value) ? parseFloat(metric.value) : `'${metric.value}'`;
          metric.jsTransformer = ` return params["${wiredStateObj.key}"].value ${metric.operator === 'LESS_THAN' ? '<' : '>'} ${compValue} `;
          break;
        case 'CONTAINS':
        case 'NOT_CONTAINS':
          if (typeof metric.value === 'string' || typeof metric.value === 'number') {
            metric.jsTransformer = ` return (""+params["${wiredStateObj.key}"].value).toLowerCase().indexOf((""+${metric.value}).toLowerCase()) ${metric.operator === 'NOT_CONTAINS' ? '<' : '>='} 0 `;
          } else {
            metric.jsTransformer = ` return (""+params["${wiredStateObj.key}"].value).find(v => (""+v).toLowerCase() == (""+${metric.value}).toLowerCase() ) ${metric.operator === 'NOT_CONTAINS' ? '==' : '!='} undefined `;
          }
          break;
        case 'IS_NULL':
        case 'IS_NOT_NULL':
          metric.jsTransformer = ` return params["${wiredStateObj.key}"].value ${metric.operator === 'IS_NULL' ? '=' : '!'}= null`;
          break;
        default:
          metric.jsTransformer = ' return true ';
          break;
      }
      metric.jsTransformer += ` ? "${metric.description}" : false; `;
    },
    emitSelection() {
      let newVirtualDeviceState = this.selectedVirtualDeviceState;
      if (this.extendedMode !== true) {
        newVirtualDeviceState = [];
      }
      for (let i = this.healthMetrics.length - 1; i >= 0; i--) {
        const metric = this.healthMetrics[i];
        metric.wizard = this.extendedMode !== true;
        if (!this.extendedMode) {
          this.createUniqueMetricKey(metric, i, this.healthMetrics.length);
          if (metric.operator === 'VALUE_OLDER_THAN') {
            metric.wiredState = this.vDeviceHealthStates.find(s => s.key === '_raw')[this.stateIdentAttr];
          }
          // push this state, if it does not exist yet in the final map
          let foundWiredStateObj = this.mapStateToPersistRepresentation(metric.wiredState);
          if (foundWiredStateObj) {
            newVirtualDeviceState.push(foundWiredStateObj);
          }
          const wiredStateObj = foundWiredStateObj || {};
          this.jsTransformerFromWizard(metric, wiredStateObj);
        } else {
          // in any case, also reset the wizard back to its default (empty) state
          // the user has been warned, that the config will be deleted
          metric.value = undefined;
          metric.operator = undefined;
          metric.key = undefined;
        }
      }
      if (!isEqual(newVirtualDeviceState, this.selectedVirtualDeviceState)) {
        // will trigger a re-call of emitSelection
        this.selectedVirtualDeviceState = newVirtualDeviceState;
        return;
      }
      const aggregateStateValue = {};
      this.selectedVirtualDeviceState.forEach(aggVar => {
        aggregateStateValue[aggVar.key] = aggVar.value;
      });
      this.aggregateStateValue = aggregateStateValue;
      this.$nextTick(() => {
        this.$emit('bridge-binding-changed', {
          config: this.publishValue,
          identifier: get(this.selectedVirtualDevice, 'id'),
          valueType: get(this.selectedVirtualDeviceState, '[0].properties.valueType'),
          variable: get(this.selectedVirtualDeviceState, '[0].key'),
          value: this.aggregateStateValue
        });
      });
      return this.aggregateStateValue;
    },
    metricIdentifierIsUnique(identifier) {
      return this.healthMetrics.filter(m => m.identifier === identifier).length <= 1;
    },
    addMetric(index) {
      this.healthMetrics.splice(index, 0, {
        type: 'error',
        jsTransformer: 'return null;',
        wizard: this.extendedMode === true,
        operator: 'VALUE_OLDER_THAN'
      });
      this.emitSelection();
    },
    deleteMetric(index) {
      pullAt(this.healthMetrics, [index]);
      this.healthMetrics = cloneDeep(this.healthMetrics);
      this.emitSelection();
    },
    saveState() {
      this.emitSelection();
      Object.keys(this.publishValue).forEach(key => {
        this.value[key] = this.publishValue[key];
      });
    },
    // if used in device-type, load device-type variables instead of states of the vdevice
    deviceTypeVariables() {
      const deviceTypeObjectCopy = cloneDeep(this.deviceTypeObject);
      let parserVars = get(deviceTypeObjectCopy, 'parser.config.variables') || {};
      if (get(deviceTypeObjectCopy, 'parser.type') === 'multi') {
        parserVars = {};
        const parserKeys = Object.keys(get(deviceTypeObjectCopy, 'parser.config') || {}).filter(k => k !== '__switch');
        parserKeys.forEach(pKey => {
          if (isObject(get(deviceTypeObjectCopy, `parser.config.${pKey}.variables`))) {
            parserVars = {
              ...parserVars,
              ...(get(deviceTypeObjectCopy, `parser.config.${pKey}.variables`) || {})
            };
          }
        });
      }
      if (Object.keys(parserVars).length > 0) {
        parserVars['_raw'] = {
          value_type: {
            name: 'json'
          }
        };
      }
      /*
      ORIGIN: {
        "temperature": {
          "name": "temperature",
          "value_type": { // << MUST BE FILLED!
            "name": "°C"
          }
        }
      }
      TARGET: {
        "temperature": {
          "time": "2019-05-28T14:17:36.000Z",
          "value": 29.75,
          "value_type_name": "°C",
          "variable": "temperature"
        }
      } */
      Object.keys(parserVars).forEach(parserKey => {
        parserVars[parserKey].key = parserKey;
        parserVars[parserKey].value = {};
        parserVars[parserKey].properties = {};
        if (parserVars[parserKey]['value_type']) {
          parserVars[parserKey].properties.unit = parserVars[parserKey]['value_type']['name'];
        }
      });
      return Object.keys(parserVars).map(key => {
        return this.mapStateToUi(parserVars[key]);
      });
    },
    syncFromDeviceType() {
      ApiService.virtualDeviceSyncHealthConfig(this, this.lclModel.dtwinId).then(() => {
        this.$emit('stateUpdated');
      }).catch(error => {
        console.error(error);
      });
    }
  }
};